
import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Clock, Tag, Zap, Database } from 'lucide-react';
import { fetchArticles } from '../services/dataService';
import { BlogPost } from '../types';
import { Link } from 'react-router-dom';

const Articles: React.FC = () => {
  const [articles, setArticles] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles().then(data => {
        setArticles(data);
        setLoading(false);
    });
  }, []);

  // Split featured article from the rest
  const featuredArticle = articles[0];
  const feedArticles = articles.slice(1);

  return (
    <div className="min-h-screen bg-white pt-32 px-4 md:px-12 pb-20 font-sans">
      <div className="max-w-[1600px] mx-auto">
        
        {/* HEADER TATTICO */}
        <header className="mb-16 border-b-2 border-black pb-8 flex flex-col md:flex-row justify-between md:items-end animate-slide-up">
          <div>
             <div className="flex items-center gap-2 text-xs font-bold tracking-[0.3em] uppercase text-black mb-4">
                <div className="w-2 h-2 bg-red-600 animate-pulse"></div>
                Archivio Intelligence
             </div>
             <h1 className="text-6xl md:text-9xl font-serif leading-none tracking-tighter">REPORT</h1>
          </div>
          <div className="mt-8 md:mt-0 flex gap-px bg-gray-200 p-1">
            <button className="px-4 py-2 bg-black text-white text-[10px] uppercase font-bold tracking-widest">Tutti</button>
            <button className="px-4 py-2 bg-white text-gray-500 hover:text-black text-[10px] uppercase font-bold tracking-widest transition-colors">Analisi</button>
            <button className="px-4 py-2 bg-white text-gray-500 hover:text-black text-[10px] uppercase font-bold tracking-widest transition-colors">Tech</button>
            <button className="px-4 py-2 bg-white text-gray-500 hover:text-black text-[10px] uppercase font-bold tracking-widest transition-colors">Etica</button>
          </div>
        </header>

        {loading ? (
            <div className="flex justify-center py-20"><div className="w-3 h-3 bg-black animate-ping"></div></div>
        ) : articles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 border border-dashed border-gray-300 bg-gray-50 animate-fade-in text-center">
                <Database size={48} className="text-gray-300 mb-6" />
                <h3 className="text-xl font-serif font-bold mb-2">Archivio Vuoto</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                    Non sono stati rilevati articoli pubblicati. Se li hai appena cancellati da WordPress, l'operazione è avvenuta con successo.
                </p>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-white px-3 py-1 border border-gray-200">
                    Stato Sistema: Sincronizzato
                </div>
            </div>
        ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                
                {/* --- FEATURED ARTICLE (MAGAZINE STYLE) --- */}
                {featuredArticle && (
                    <div className="lg:col-span-8 animate-fade-in">
                        <Link to={`/articoli/${featuredArticle.id}`} className="group block relative">
                            <div className="border border-gray-200 p-8 md:p-12 hover:border-black transition-colors duration-500 bg-gray-50/30">
                                <div className="flex justify-between items-start mb-8">
                                    <span className="px-3 py-1 bg-black text-white text-xs font-bold uppercase tracking-widest">
                                        In Evidenza
                                    </span>
                                    <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={24} />
                                </div>
                                
                                <h2 className="text-4xl md:text-6xl font-serif font-medium mb-6 leading-tight group-hover:text-gray-700 transition-colors">
                                    {featuredArticle.title}
                                </h2>
                                
                                <p className="text-xl font-light text-gray-600 leading-relaxed mb-8 max-w-2xl">
                                    {featuredArticle.excerpt}
                                </p>

                                <div className="flex items-center gap-6 text-xs font-bold uppercase tracking-widest text-gray-500 border-t border-gray-200 pt-6">
                                    <span className="text-black">{featuredArticle.author}</span>
                                    <span>//</span>
                                    <span>{featuredArticle.date}</span>
                                    <span>//</span>
                                    <span>{featuredArticle.readTime} Read</span>
                                </div>
                            </div>
                        </Link>
                    </div>
                )}

                {/* --- TACTICAL FEED LIST (RIGHT COLUMN) --- */}
                {feedArticles.length > 0 && (
                    <div className="lg:col-span-4 space-y-px bg-gray-200 border border-gray-200">
                        {feedArticles.map((article) => (
                            <Link 
                                to={`/articoli/${article.id}`}
                                key={article.id} 
                                className="group block bg-white p-6 hover:bg-gray-50 transition-colors relative overflow-hidden"
                            >
                                <div className="absolute top-0 left-0 w-1 h-full bg-black transform -translate-x-full group-hover:translate-x-0 transition-transform duration-200"></div>
                                
                                <div className="flex items-center gap-2 text-[9px] uppercase font-bold tracking-widest text-gray-400 mb-2">
                                    <Tag size={10} /> {article.category}
                                </div>
                                
                                <h3 className="text-xl font-serif font-bold mb-2 group-hover:underline decoration-1 underline-offset-4 leading-tight">
                                    {article.title}
                                </h3>
                                
                                <div className="flex justify-between items-center mt-4">
                                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{article.date}</span>
                                    <span className="text-[10px] font-bold uppercase text-gray-400 group-hover:text-black flex items-center gap-1 tracking-wide">
                                        Read <Zap size={10} className="fill-black text-black" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                        
                        {/* LOAD MORE BUTTON BLOCK */}
                        <button className="w-full bg-black text-white py-4 text-xs font-bold uppercase tracking-widest hover:bg-red-600 transition-colors">
                            Carica Archivio Completo
                        </button>
                    </div>
                )}

            </div>
        )}
      </div>
    </div>
  );
};

export default Articles;
